/**
 * L'ÉCRAN DE RATTACHEMENT DES CLIENTS À PENNYLANE.
 *
 * Maquette `docs/maquettes/rattachement-clients-pennylane.html`, validée le
 * 11/09/2026. Décision du même jour : « stafa ne numerote plus il utilise
 * pennylane ». Une facture Pennylane désigne son client par un identifiant
 * chez eux ; sans rattachement, Stafa ne peut pas facturer.
 *
 * UN ONGLET DE PARAMÈTRES, et non un champ sur la fiche client. Choisi le
 * 11/09 : le rattachement se fait une fois, pour tous les clients, et cet
 * écran montre d'un coup ce qui manque — ce qu'une fiche ouverte une par une
 * ne dira jamais.
 *
 * IL NE CALCULE RIEN. Les trois états, la concordance des SIREN, l'avancement
 * et les choix du champ de recherche sont dans `rattachement.ts`, éprouvés
 * sans navigateur. Ce fichier dessine et appelle.
 *
 * CHAQUE CHOIX S'ENREGISTRE AUSSITÔT, sans bouton « Valider ». Un rattachement
 * est une décision isolée, pas une saisie à cohérence d'ensemble : un bouton
 * global laisserait croire qu'on peut tout annuler alors que neuf lignes
 * seraient déjà bonnes.
 */

import { useEffect, useState } from 'react';
import { ChoixCherchable } from './ChoixCherchable.js';
import {
  lireRattachements,
  rattacherAPennylane,
  type EchecApi,
  type Rattachements,
} from './api.js';
import {
  avancementRattachement,
  choixPennylane,
  indexerPennylane,
  lignesRattachement,
  type LigneRattachement,
} from './rattachement.js';

/** L'état d'une ligne en cours d'enregistrement — pour éteindre son champ. */
type EnCours = ReadonlySet<string>;

function Etat({ ligne }: { readonly ligne: LigneRattachement }) {
  if (ligne.etat === 'rattache') {
    return <span className="etat-rattachement rattache">Rattaché</span>;
  }
  if (ligne.etat === 'perdu') {
    return (
      <span className="etat-rattachement perdu">Introuvable chez Pennylane</span>
    );
  }
  return <span className="etat-rattachement a-faire">À rattacher</span>;
}

export function OngletRattachementPennylane({
  jeton,
  quandAccesPerdu,
  messageRefus,
}: {
  /**
   * LE JETON EST UNE FONCTION, pas une chaîne : il ne vaut qu'une heure et se
   * renouvelle. Le recevoir en chaîne aurait marché à l'ouverture puis échoué
   * en silence sur un écran laissé ouvert — et le rattachement est justement
   * un écran qu'on laisse ouvert le temps de faire ses dix clients.
   */
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  /** La table REFUS de l'écran — écrite une fois, partagée par tout Paramètres. */
  readonly messageRefus: (cause: EchecApi) => string;
}) {
  const [donnees, setDonnees] = useState<Rattachements | null>(null);
  const [chargement, setChargement] = useState(true);
  const [refus, setRefus] = useState<string | null>(null);
  const [avecInactifs, setAvecInactifs] = useState(false);
  const [enCours, setEnCours] = useState<EnCours>(new Set());

  async function charger() {
    setChargement(true);
    setRefus(null);
    const j = await jeton();
    if (j === null) {
      quandAccesPerdu();
      setChargement(false);
      return;
    }
    const resultat = await lireRattachements(j);
    if (resultat.etat === 'ok') {
      setDonnees(resultat.valeur);
    } else {
      if (resultat.cause === 'acces') quandAccesPerdu();
      setRefus(messageRefus(resultat.cause));
    }
    setChargement(false);
  }

  /**
   * UNE SEULE LECTURE À L'OUVERTURE. Le rechargement passe par le bouton du
   * pied : ramasser 214 clients chez Pennylane demande plusieurs allers-
   * retours, et le refaire à chaque rendu rendrait l'écran inutilisable.
   *
   * `jeton` SEUL EN DÉPENDANCE, délibérément. `charger` se referme aussi sur
   * `messageRefus`, que le parent recrée à chaque rendu : le mettre en
   * dépendance relancerait la lecture en boucle. Les deux autres ne servent
   * qu'à mettre un refus en mots, ce qui ne change pas d'un rendu à l'autre.
   */
  useEffect(() => {
    void charger();
  }, [jeton]);

  /**
   * Pose ou retire un rattachement, puis RELIT la ligne depuis la réponse.
   *
   * ON NE MET PAS L'ÉCRAN À JOUR AVANT LA RÉPONSE. Un affichage optimiste
   * montrerait « Rattaché » sur une ligne que la base vient de refuser — et le
   * refus le plus probable ici, « ce client Pennylane est déjà pris », est
   * précisément celui qu'il ne faut pas masquer.
   */
  async function rattacher(clientId: string, pennylaneId: string | null) {
    const j = await jeton();
    if (j === null) {
      quandAccesPerdu();
      return;
    }

    setEnCours((s) => new Set(s).add(clientId));
    setRefus(null);

    const resultat = await rattacherAPennylane(j, clientId, pennylaneId);

    setEnCours((s) => {
      const suite = new Set(s);
      suite.delete(clientId);
      return suite;
    });

    if (resultat.etat !== 'ok') {
      if (resultat.cause === 'acces') quandAccesPerdu();
      setRefus(messageRefus(resultat.cause));
      return;
    }

    /**
     * ON RELIT LA VALEUR DANS LA RÉPONSE, on ne recopie pas celle qu'on a
     * envoyée. C'est la base qui dit ce qui a été écrit : si un jour une
     * contrainte normalisait la valeur, l'écran afficherait la vraie et non
     * celle qu'il croyait poser.
     */
    const fiche = resultat.valeur.fiche as unknown as {
      pennylane_client_id?: unknown;
    };
    const pose =
      fiche.pennylane_client_id === null || fiche.pennylane_client_id === undefined
        ? null
        : String(fiche.pennylane_client_id);

    setDonnees((d) =>
      d === null
        ? d
        : {
            ...d,
            clients: d.clients.map((c) =>
              c.id === clientId ? { ...c, pennylane_client_id: pose } : c,
            ),
          },
    );
  }

  if (chargement) {
    return <p className="attente">Lecture des clients chez Pennylane…</p>;
  }

  if (donnees === null) {
    return (
      <div className="panneau-rattachement">
        {refus !== null && <p className="bandeau bandeau-refus">{refus}</p>}
        <button type="button" className="bouton bouton-discret" onClick={() => void charger()}>
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <VueRattachement
      donnees={donnees}
      refus={refus}
      avecInactifs={avecInactifs}
      enCours={enCours}
      quandRattacher={(clientId, pennylaneId) => void rattacher(clientId, pennylaneId)}
      quandInactifs={setAvecInactifs}
      quandRecharger={() => void charger()}
    />
  );
}

/**
 * LA VUE — elle dessine, elle n'appelle rien.
 *
 * SÉPARÉE DU CONTENEUR le 11/09/2026, et pas par goût de l'architecture : sans
 * cette coupure, l'écran ne peut pas entrer dans `apercu.tsx`, qui n'a ni
 * réseau ni jeton. Or « un écran qu'on ne rend pas est un écran que personne
 * ne relit » — c'est ce qui avait laissé passer le défaut du 10/09 sur
 * l'onglet « À facturer », et celui du 11/09 sur l'éditeur de facture.
 *
 * TOUTES SES PROPRIÉTÉS SONT TYPÉES, sans `as unknown as` : c'est exactement
 * ce cast qui avait laissé passer le défaut d'aperçu du lot 26.
 */
export function VueRattachement({
  donnees,
  refus,
  avecInactifs,
  enCours,
  quandRattacher,
  quandInactifs,
  quandRecharger,
}: {
  readonly donnees: Rattachements;
  readonly refus: string | null;
  readonly avecInactifs: boolean;
  readonly enCours: EnCours;
  /** `null` en second argument DÉTACHE. */
  readonly quandRattacher: (clientId: string, pennylaneId: string | null) => void;
  readonly quandInactifs: (avec: boolean) => void;
  readonly quandRecharger: () => void;
}) {
  const index = indexerPennylane(donnees.pennylane.clients);
  const lignes = lignesRattachement(donnees.clients, index, avecInactifs);
  const avancement = avancementRattachement(donnees.clients, index);
  const choix = choixPennylane(donnees.pennylane.clients);
  const inactifs = donnees.clients.filter((c) => !c.actif).length;

  return (
    <div className="panneau-rattachement">
      {/* LA LISTE PENNYLANE EST-ELLE COMPLÈTE ? Si elle ne l'est pas, tout le
          reste de l'écran ment : un client absent de la liste passerait pour
          supprimé chez Pennylane. On le dit AVANT le tableau. */}
      {donnees.pennylane.tronque && (
        <p className="bandeau bandeau-refus">
          La liste des clients Pennylane est incomplète
          {donnees.pennylane.message === null ? '' : ` — ${donnees.pennylane.message}`}{' '}
          Les états affichés ci-dessous ne sont pas fiables tant que ce n’est pas
          réglé.
        </p>
      )}

      {refus !== null && <p className="bandeau bandeau-refus">{refus}</p>}

      <div className="avancement-rattachement">
        <span className="jauge-rattachement">
          <span style={{ width: `${String(avancement.pourcentage)}%` }} />
        </span>
        <span>
          <b>
            {avancement.rattaches} rattaché{avancement.rattaches > 1 ? 's' : ''}
          </b>{' '}
          sur {avancement.total} client{avancement.total > 1 ? 's' : ''} actif
          {avancement.total > 1 ? 's' : ''}
        </span>
        <span className="a-droite-rattachement">
          {avancement.bloques === 0 ? (
            'Tous les clients actifs peuvent être facturés.'
          ) : (
            <>
              {avancement.bloques} client{avancement.bloques > 1 ? 's' : ''} ne
              {avancement.bloques > 1 ? ' peuvent' : ' peut'} pas encore être
              facturé{avancement.bloques > 1 ? 's' : ''}
            </>
          )}
        </span>
      </div>

      <div className="cadre-rattachement">
        <table className="tableau-rattachement">
          <thead>
            <tr>
              <th className="groupe-rattachement" colSpan={3}>
                Dans Stafa
              </th>
              <th className="groupe-rattachement cote-pennylane" colSpan={2}>
                Chez Pennylane
              </th>
              <th className="groupe-rattachement" />
            </tr>
            <tr>
              <th>Code</th>
              <th>Client</th>
              <th>SIREN</th>
              <th className="cote-pennylane">Client rattaché</th>
              <th>SIREN</th>
              <th>État</th>
            </tr>
          </thead>
          <tbody>
            {lignes.length === 0 ? (
              <tr>
                <td colSpan={6} className="vide-rattachement">
                  Aucun client à afficher.
                </td>
              </tr>
            ) : (
              lignes.map((ligne) => (
                <tr key={ligne.client.id} className={ligne.client.actif ? '' : 'inactif'}>
                  <td className="chiffres-rattachement">{ligne.client.code ?? '—'}</td>
                  <td>
                    {ligne.client.nom}
                    {!ligne.client.actif && (
                      <span className="secondaire-rattachement">inactif</span>
                    )}
                  </td>
                  <td className="chiffres-rattachement">
                    {ligne.client.siren ?? (
                      <span className="sans-siren">non renseigné</span>
                    )}
                  </td>
                  <td className="cote-pennylane">
                    {ligne.etat === 'perdu' ? (
                      /* L'IDENTIFIANT RESTE AFFICHÉ, et n'est pas effacé :
                         c'est le seul indice permettant de comprendre ce qui
                         s'est passé chez Pennylane. */
                      <span className="perdu-rattachement">
                        identifiant {ligne.client.pennylane_client_id}
                        <button
                          type="button"
                          className="detacher-rattachement"
                          title="Détacher"
                          aria-label={`Détacher ${ligne.client.nom}`}
                          disabled={enCours.has(ligne.client.id)}
                          onClick={() => quandRattacher(ligne.client.id, null)}
                        >
                          ✕
                        </button>
                      </span>
                    ) : (
                      <ChoixCherchable
                        id={`pennylane-${ligne.client.id}`}
                        choix={choix}
                        valeur={ligne.client.pennylane_client_id ?? ''}
                        invite="Chercher un client Pennylane…"
                        desactive={enCours.has(ligne.client.id)}
                        quandChoisir={(id) =>
                          quandRattacher(ligne.client.id, id === '' ? null : id)
                        }
                      />
                    )}
                  </td>
                  <td className="chiffres-rattachement">
                    {ligne.chezPennylane?.siren ?? '—'}
                    {/* LE DÉSACCORD SE DIT, et il ne bloque pas : c'est peut-être
                        le SIREN de Stafa qui est faux. On signale, on n'empêche
                        pas. */}
                    {ligne.sirenConcorde === false && (
                      <span className="discordance">ne concorde pas</span>
                    )}
                  </td>
                  <td>
                    <Etat ligne={ligne} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="pied-rattachement">
        <label className="inactifs-rattachement">
          <input
            type="checkbox"
            checked={avecInactifs}
            onChange={(e) => quandInactifs(e.target.checked)}
          />
          Afficher aussi les clients inactifs ({inactifs})
        </label>
        <span className="a-droite-rattachement">
          {donnees.pennylane.retenus} client
          {donnees.pennylane.retenus > 1 ? 's' : ''} lu
          {donnees.pennylane.retenus > 1 ? 's' : ''} chez Pennylane
          {donnees.pennylane.pages > 1 && ` en ${donnees.pennylane.pages} appels`}
          {/* L'ÉCART ENTRE REÇUS ET RETENUS EST UN SIGNAL : des clients ont été
              écartés parce qu'un champ attendu manquait. Le taire ferait croire
              à un fichier plus petit qu'il n'est. */}
          {donnees.pennylane.recus > donnees.pennylane.retenus &&
            ` — ${donnees.pennylane.recus - donnees.pennylane.retenus} écarté(s), forme inattendue`}
          {' · '}
          <button
            type="button"
            className="lien-rattachement"
            onClick={quandRecharger}
          >
            Recharger
          </button>
        </span>
      </div>
    </div>
  );
}
